import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, OnDestroy } from '@angular/core';
import { FormGroup, FormsModule, NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { finalize, Subject, takeUntil } from 'rxjs';
import { loadTaxType } from '../../after-login/store/tax/tax.action';
import {
  AuthService,
  TWO_FACTOR_CODE_PATTERN,
  TwoFactorLoginChallenge,
  twoFactorErrorMessage,
} from '../../services/auth.service';
import { EncryptDecryptService } from '../../services/encrypt-decrypt.service';
import { NotificationsService } from '../../services/notifications.service';
import { TosterService } from '../../services/toster.service';
import { Store } from '@ngrx/store';
import { SelectModule } from 'primeng/select';
import { TranslationService } from '../../services/translation.service';

@Component({
  selector: 'app-login',
  imports: [ReactiveFormsModule,SelectModule,TranslateModule,FormsModule, CommonModule, RouterLink, TranslateModule],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
})
export class LoginComponent implements OnDestroy {
    public translationService: TranslationService = inject(TranslationService);
  
  private readonly destroy$ = new Subject<void>();
  private readonly allowedRoles = new Set([
    'hotel-owner',
    'marketplace',
    'employee',
    'customer',
  ]);
langOptions = [
    {
      name: 'English',
      value: 'en',
      code: 'us',
    },
    {
      name: 'العربية',
      value: 'ar',
      code: 'sa',
    },
  ];
  selectedLang: string;
  readonly loginForm: FormGroup;
  showPassword = false;
  submitted = false;
  loginSubmitting = false;

  // Second login step, shown only when the password step answers with a two-factor
  // challenge. The challenge token is kept in memory only: it is not a session.
  readonly twoFactorForm: FormGroup;
  twoFactorStep = false;
  twoFactorSubmitted = false;
  twoFactorSubmitting = false;
  private twoFactorChallengeToken: string | null = null;
  private twoFactorExpiryTimer: ReturnType<typeof setTimeout> | null = null;
  private static readonly DEFAULT_CHALLENGE_TTL_SECONDS = 300;

  private store = inject(Store);
  private translate = inject(TranslateService);

  constructor(
    private fb: NonNullableFormBuilder,
    private router: Router,
    private authService: AuthService,
    private tosterService: TosterService,
    private notificationsService: NotificationsService,
    private encryptService: EncryptDecryptService,
  ) {
    this.selectedLang = this.translationService.currentLang;
    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required]],
      rememberMe: [false],
    });
    this.twoFactorForm = this.fb.group({
      totp: ['', [Validators.required, Validators.pattern(TWO_FACTOR_CODE_PATTERN)]],
    });

    this.loadSavedCredentials();
  }

  get f() {
    return this.loginForm.controls;
  }

  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }
  public onLangChange(lang: string) {
    this.translationService.changeLang(lang);
  }
  openSignUpPopup() {
    window.open(
      'https://www.syncinns.com/partner-with-us',
      'SignUpPopup',
      'width=800,height=600,resizable=yes,scrollbars=yes',
    );
  }

  login() {
    if (this.loginSubmitting) return;
    this.clearTwoFactorChallenge();
    this.submitted = true;
    this.authService.clearSession();
    this.tosterService.clearAll();
    if (this.loginForm.invalid) {
      console.log("this.loginForm.invalid ", this.loginForm.invalid)
      this.loginForm.markAllAsTouched();
      const emailControl = this.f['email'];
      const passwordControl = this.f['password'];

      if (
        emailControl?.errors?.['required'] &&
        passwordControl?.errors?.['required']
      ) {
        this.tosterService.showErrorKey('login.emailPassword');
      } else if (emailControl?.errors?.['required']) {
        this.tosterService.showErrorKey('login.emailRequired');
      } else if (emailControl?.errors?.['email']) {
        this.tosterService.showErrorKey('login.emailInvalid');
      } else if (passwordControl?.errors?.['required']) {
        this.tosterService.showErrorKey('login.passwordRequired');
      }
      return;
    }

    const { email, password, rememberMe } = this.loginForm.getRawValue();
    this.handleRememberMe(email, rememberMe);

    this.loginSubmitting = true;
    this.authService
      .login({ email, password })
      .pipe(
        finalize(() => (this.loginSubmitting = false)),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (res: any) => {
          // Only accounts with 2FA enabled get a challenge; every other login is unchanged.
          if (res?.data?.two_factor_required === true) {
            this.startTwoFactorStep(res.data);
            return;
          }
          this.handleLoginSuccess(res);
        },
        error: (errorResponse: HttpErrorResponse) => {
          this.tosterService.showError(errorResponse.error.message);
        },
      });
  }

  verifyTwoFactor(): void {
    if (this.twoFactorSubmitting || !this.twoFactorChallengeToken) return;
    this.twoFactorSubmitted = true;

    const totp = String(this.twoFactorForm.getRawValue().totp ?? '').trim();
    if (!TWO_FACTOR_CODE_PATTERN.test(totp)) {
      this.twoFactorForm.markAllAsTouched();
      return;
    }

    this.twoFactorSubmitting = true;
    this.authService
      .verifyTwoFactorLogin({ challenge_token: this.twoFactorChallengeToken, totp })
      .pipe(
        finalize(() => (this.twoFactorSubmitting = false)),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (res: any) => {
          this.clearTwoFactorChallenge();
          this.handleLoginSuccess(res);
        },
        error: (errorResponse: HttpErrorResponse) => {
          // A wrong code and a dead challenge get the same answer; the step stays open so the
          // user can retry, and the expiry timer sends them back to the password step.
          this.twoFactorForm.reset();
          this.twoFactorSubmitted = false;
          this.tosterService.showError(twoFactorErrorMessage(errorResponse, this.translate));
        },
      });
  }

  cancelTwoFactor(): void {
    if (this.twoFactorSubmitting) return;
    this.clearTwoFactorChallenge();
  }

  ngOnDestroy(): void {
    this.clearTwoFactorChallenge();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private startTwoFactorStep(challenge: TwoFactorLoginChallenge): void {
    this.clearTwoFactorChallenge();
    this.twoFactorChallengeToken = challenge.challenge_token;
    this.twoFactorStep = true;
    // The password has done its job; a new challenge needs it entered again.
    this.f['password'].reset();
    this.submitted = false;

    const ttlSeconds =
      Number(challenge.expires_in) > 0
        ? Number(challenge.expires_in)
        : LoginComponent.DEFAULT_CHALLENGE_TTL_SECONDS;
    this.twoFactorExpiryTimer = setTimeout(() => {
      this.clearTwoFactorChallenge();
      this.tosterService.showErrorKey('twoFactor.login.expired');
    }, ttlSeconds * 1000);
  }

  private clearTwoFactorChallenge(): void {
    if (this.twoFactorExpiryTimer) {
      clearTimeout(this.twoFactorExpiryTimer);
      this.twoFactorExpiryTimer = null;
    }
    this.twoFactorChallengeToken = null;
    this.twoFactorStep = false;
    this.twoFactorSubmitted = false;
    this.twoFactorForm?.reset();
  }

  private handleRememberMe(email: string, rememberMe: boolean): void {
    if (rememberMe) {
      const encryptData = this.encryptService.encryptData(
        JSON.stringify({ email }),
      );
      localStorage.setItem('savedCredential', encryptData);
      return;
    }

    localStorage.removeItem('savedCredential');
  }

  private handleLoginSuccess(res: any): void {
    const userType = res?.data?.user?.userTypeSlug;
    // The session cookie now belongs to this user, so the tab does too (also for the logout of
    // a refused role below). Set before the shared UserId changes and the other tabs react.
    this.authService.startTabSession(res?.data?.user?._id);

    if (!(res?.status === true && this.allowedRoles.has(userType))) {
      this.tosterService.showSuccessKey('common.toasts.invalidCredentials');
      this.authService.logout();
      return;
    }



    this.tosterService.showSuccess(res.message);
    // this.authService.setAccessToken(res.data.accessToken);
    // this.authService.setRefreshToken(res.data.refreshToken);
    this.authService.setUserId(res.data.user._id);
    this.authService.setUserType(userType);
    localStorage.setItem('verified', res.data.user?.account_status);
    localStorage.setItem('loggedIn', 'true');
    const encryptLoginData = this.encryptService.encryptData(
      JSON.stringify(res?.data),
    );


    const loader = document.getElementById('startup-loader');
    if (loader) {
      loader.style.display = 'none';
    }


    localStorage.setItem('loginData', encryptLoginData);

    const navigateTo =
      userType === 'customer'
        ? ['/syncinns-pms/booking-engine']
        : ['/syncinns-pms'];
    const navigationStart = performance.now();

    this.router.navigate(navigateTo).finally(() => {
      const navigationDuration = performance.now() - navigationStart;
      console.log(
        `[Login] Navigation to ${navigateTo.join('/')} took ${navigationDuration.toFixed(2)}ms`,
      );
      this.schedulePostLoginBackgroundTasks(res.data.user._id);
    });
  }

  private schedulePostLoginBackgroundTasks(userId: string): void {
    const runTasks = () => this.runPostLoginBackgroundTasks(userId);

    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      window.requestIdleCallback(() => runTasks());
      return;
    }

    setTimeout(runTasks, 800);
  }

  private runPostLoginBackgroundTasks(userId: string): void {
    this.notificationsService
      .requestPermissionAndGetToken(userId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (token) =>
          console.log('FCM token handled for logged-in user:', token),
        error: (err) =>
          console.error('Error handling FCM token after login:', err),
      });

    this.store.dispatch(loadTaxType());
  }

  private loadSavedCredentials(): void {
    const credential = localStorage.getItem('savedCredential');

    if (!credential) return;

    try {
      const decryptData = this.encryptService.decryptData(credential);
      const credentials = JSON.parse(decryptData);
      this.loginForm.patchValue({
        email: credentials.email,
        rememberMe: true,
      });
    } catch {
      sessionStorage.clear();
      localStorage.clear();
    }
  }

}
